// src/components/extraction/rechnungAnsicht.js
// Gemeinsames Ansichtsmodell für eine erfasste Rechnung - von RechnungTabelle.jsx UND vom
// Export genutzt, damit beide exakt dieselbe Darstellung (Status, Positionencheck, Kommentar,
// Zellmarkierung) erzeugen und nicht auseinanderlaufen können.
import { formatDate, formatCurrency } from '../../components/shared/formatters';
import getMwstSpalten from './mwstSpalten';

function parseValidationJson(validationJson) {
  if (!validationJson) return null;
  try {
    return JSON.parse(validationJson);
  } catch {
    return null;
  }
}

// Bildet Backend-Feldnamen (aus RechnungValidierung.cs) auf unsere Anzeige-Keys ab.
const FIELD_NAME_ALIASES = {
  Rechnungsnummer: 'rechnungsnummer',
  Rechnungsdatum: 'rechnungsdatum',
  Lieferant: 'lieferant',
  LieferantAdresse: 'lieferant_adresse',
  BruttobetragEur: 'bruttobetrag_eur',
  Bruttobetrag: 'bruttobetrag_eur',
  NettobetragEur: 'nettobetrag_eur',
  MwstBetragEur: 'mwst_betrag_eur',
  Skonto: 'skonto',
  UmsatzsteuerId: 'umsatzsteuer_id',
  // BankverbindungenIban[i].Iban wird bewusst NICHT gemappt (dynamischer Index pro Eintrag,
  // siehe istIbanAuswahlOffen weiter unten) - die IBAN-Auswahl bekommt ihre eigene, rein
  // clientseitige Gelb-Markierung statt einer serverseitigen Validierungs-Zuordnung.
};

function buildFieldIssueMap(eintraege) {
  const map = {};
  (eintraege || []).forEach((eintrag) => {
    const displayKey = FIELD_NAME_ALIASES[eintrag.feld];
    if (displayKey) map[displayKey] = eintrag;
  });
  return map;
}

function ermittleStatus(validation) {
  if (!validation) return { text: '–', klasse: 'status-unbekannt' };
  if (!validation.valid) return { text: 'Kritisch', klasse: 'status-kritisch' };
  const parsed = parseValidationJson(validation.validationJson);
  const hatWarnungen = (parsed?.warnungen?.length ?? 0) > 0;
  return hatWarnungen
    ? { text: 'Warnung', klasse: 'status-warnung' }
    : { text: 'OK', klasse: 'status-ok' };
}

function ermittlePositionencheck(validation) {
  const parsed = parseValidationJson(validation?.validationJson);
  const hinweis = parsed?.hinweise?.find((e) => e.feld === 'Positionen');
  if (hinweis) return { text: '✓', klasse: 'positionen-ok', titel: hinweis.meldung };
  const warnung = parsed?.warnungen?.find((e) => e.feld === 'Positionen');
  if (warnung) return { text: '✗', klasse: 'positionen-abweichung', titel: warnung.meldung };
  return { text: '–', klasse: 'positionen-unbekannt', titel: 'Keine Positionssumme geprüft' };
}

function ermittleKommentar(validation) {
  const parsed = parseValidationJson(validation?.validationJson);
  if (!parsed) {
    return validation?.remarks || '';
  }
  const meldungen = [...(parsed?.kritischeFehler ?? []), ...(parsed?.warnungen ?? [])]
    .map((e) => e.meldung);
  return meldungen.join('; ');
}

/**
 * Ergänzt eine frisch extrahierte oder aus einem Batch-Lauf stammende Rechnung um die beiden
 * rein clientseitigen IBAN-Auswahl-Felder, falls sie noch fehlen. Das Backend/Rechnung.cs kennt
 * diese Felder nicht (siehe Projekt-Historie) - "ausgewaehlte_iban" ist immer die erste gefundene
 * IBAN, bis der Anwender im Formular bewusst eine andere wählt ("iban_manuell_gewaehlt": true).
 * Idempotent: ein bereits initialisiertes Objekt kommt unverändert zurück.
 *
 * Bewusst an mehreren Stellen aufgerufen (Einzelextraktion, Stapelverarbeitung, beim
 * Übernehmen/Speichern in RechnungTab.jsx) - so bekommt JEDE Rechnung, die je in die Liste
 * gelangt, garantiert eine Default-Auswahl, auch wenn der Anwender das Formular nie geöffnet hat
 * (typisch bei der Stapelverarbeitung).
 */
export function mitIbanDefault(rechnung) {
  if (!rechnung) return rechnung;
  if (
    rechnung.ausgewaehlte_iban !== undefined &&
    rechnung.iban_manuell_gewaehlt !== undefined
  ) {
    return rechnung;
  }
  const ibans = Array.isArray(rechnung.bankverbindungen_iban)
    ? rechnung.bankverbindungen_iban
    : [];
  return {
    ...rechnung,
    ausgewaehlte_iban: rechnung.ausgewaehlte_iban ?? ibans[0]?.iban ?? null,
    iban_manuell_gewaehlt: rechnung.iban_manuell_gewaehlt ?? false,
  };
}

/**
 * True, wenn mehrere Bankverbindungen zur Auswahl stehen UND der Anwender noch keine bewusst
 * ausgewählt hat. Steuert die Gelb-Markierung sowohl der IBAN-Auswahl im Formular
 * (RechnungFieldsView.jsx) als auch der ganzen Tabellenzeile (RechnungTabelle.jsx) - dort gibt es
 * bewusst keine eigene IBAN-Spalte (siehe Projekt-Historie), daher Zeilen- statt Zellmarkierung.
 * Bei 0 oder genau 1 IBAN gibt es keine Ambiguität, also nie eine Warnung.
 */
export function istIbanAuswahlOffen(rechnung) {
  const anzahl = rechnung?.bankverbindungen_iban?.length ?? 0;
  return anzahl > 1 && !rechnung?.iban_manuell_gewaehlt;
}

/**
 * Ergänzt eine frisch extrahierte oder aus einem Batch-Lauf stammende Rechnung um einen
 * editierbaren Kommentar, falls noch keiner gesetzt ist. Default ist die bisherige, aus den
 * Validierungsmeldungen automatisch zusammengesetzte Kurzfassung (ermittleKommentar) - der
 * Anwender kann diesen Text im Formular danach frei überschreiben/ergänzen; einmal gesetzt,
 * wird der Default nie wieder nachträglich draufgeschrieben (idempotent, wie mitIbanDefault).
 * Bewusst an denselben Stellen wie mitIbanDefault aufgerufen (RechnungTab.jsx), damit auch
 * Einträge aus der Stapelverarbeitung sofort einen sinnvollen Kommentar haben, ohne dass der
 * Anwender das Formular je geöffnet haben muss.
 */
export function mitKommentarDefault(rechnung, validation) {
  if (!rechnung) return rechnung;
  if (rechnung.kommentar !== undefined) return rechnung;
  return {
    ...rechnung,
    kommentar: ermittleKommentar(validation),
  };
}

/**
 * Baut das vollständige Ansichtsmodell für einen Listen-Eintrag - genutzt sowohl für die
 * Tabellenzeile (RechnungTabelle.jsx) als auch für den Excel-Export. Jedes Feld mit möglicher
 * Markierung liefert { wert, wertFormatiert?, klasse, titel } - klasse ist 'zelle-kritisch',
 * 'zelle-warnung' oder undefined.
 *
 * eintrag.dokument (aus useDokumentListe) enthält für Rechnungen die extrahierte Rechnung -
 * hier lokal als "rechnung" destrukturiert, da diese Funktion Rechnung-spezifisch bleibt.
 */
export function baueZeilenAnsicht(eintrag) {
  const { dokument: rechnung, validation } = eintrag;
  const mwstSpalten = getMwstSpalten();

  const parsedFuerZellen = parseValidationJson(validation?.validationJson);
  const kritischeFelder = buildFieldIssueMap(parsedFuerZellen?.kritischeFehler);
  const warnFelder = buildFieldIssueMap(parsedFuerZellen?.warnungen);
  const zellKlasse = (key) =>
    kritischeFelder[key] ? 'zelle-kritisch' : warnFelder[key] ? 'zelle-warnung' : undefined;
  const zellTitel = (key) => kritischeFelder[key]?.meldung || warnFelder[key]?.meldung;

  return {
    id: eintrag.id,
    lieferant: {
      wert: rechnung.lieferant,
      klasse: zellKlasse('lieferant'),
      titel: zellTitel('lieferant'),
    },
    lieferantAdresse: {
      wert: rechnung.lieferant_adresse,
      klasse: zellKlasse('lieferant_adresse'),
      titel: zellTitel('lieferant_adresse'),
    },
    umsatzsteuerId: {
      wert: rechnung.umsatzsteuer_id,
      klasse: zellKlasse('umsatzsteuer_id'),
      titel: zellTitel('umsatzsteuer_id'),
    },
    rechnungsnummer: {
      wert: rechnung.rechnungsnummer,
      klasse: zellKlasse('rechnungsnummer'),
      titel: zellTitel('rechnungsnummer'),
    },
    rechnungsdatum: {
      wert: rechnung.rechnungsdatum,
      wertFormatiert: formatDate(rechnung.rechnungsdatum),
      klasse: zellKlasse('rechnungsdatum'),
      titel: zellTitel('rechnungsdatum'),
    },
    netto: {
      wert: rechnung.nettobetrag_eur,
      wertFormatiert: formatCurrency(rechnung.nettobetrag_eur),
      klasse: zellKlasse('nettobetrag_eur'),
      titel: zellTitel('nettobetrag_eur'),
    },
    skonto: {
      wert: rechnung.skonto,
      wertFormatiert: formatCurrency(rechnung.skonto),
      klasse: zellKlasse('skonto'),
      titel: zellTitel('skonto'),
    },
    mwst: mwstSpalten.map(({ key, label }) => ({
      satz: key,
      label,
      wert: rechnung.mwst_betrag_eur?.[key],
      wertFormatiert: formatCurrency(rechnung.mwst_betrag_eur?.[key]),
      klasse: zellKlasse('mwst_betrag_eur'),
      titel: zellTitel('mwst_betrag_eur'),
    })),
    brutto: {
      wert: rechnung.bruttobetrag_eur,
      wertFormatiert: formatCurrency(rechnung.bruttobetrag_eur),
      klasse: zellKlasse('bruttobetrag_eur'),
      titel: zellTitel('bruttobetrag_eur'),
    },
    // Rein clientseitig berechnet (siehe istIbanAuswahlOffen) - unabhängig von der
    // Backend-Validierung, da das Backend die Auswahl gar nicht kennt.
    iban: {
      wert: rechnung.ausgewaehlte_iban ?? rechnung.bankverbindungen_iban?.[0]?.iban ?? null,
      klasse: istIbanAuswahlOffen(rechnung) ? 'zelle-warnung' : undefined,
      titel: istIbanAuswahlOffen(rechnung)
        ? 'Es liegen mehrere Bankverbindungen vor - bitte im Formular die richtige IBAN auswählen.'
        : undefined,
    },
    ibanAuswahlOffen: istIbanAuswahlOffen(rechnung),
    status: ermittleStatus(validation),
    positionencheck: ermittlePositionencheck(validation),
    // Editierbarer Wert (siehe mitKommentarDefault) hat Vorrang; die Auto-Ermittlung aus den
    // Validierungsmeldungen bleibt nur als defensiver Fallback für den (nach Einführung von
    // mitKommentarDefault eigentlich nicht mehr vorkommenden) Fall, dass rechnung.kommentar noch
    // nie gesetzt wurde.
    kommentar: rechnung.kommentar ?? ermittleKommentar(validation),
  };
}

export default baueZeilenAnsicht;